import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import HelpDrawer from "@/components/HelpDrawer";
import SplashScreen from "@/components/SplashScreen";

import Storefront from "@/pages/Storefront";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Privacy from "@/pages/Privacy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminInventory from "@/pages/AdminInventory";
import AdminInventoryEdit from "@/pages/AdminInventoryEdit";
import MobileCapture from "@/pages/MobileCapture";
import AdminPipeline from "@/pages/AdminPipeline";
import AdminSettings from "@/pages/AdminSettings";
import AdminBusiness from "@/pages/AdminBusiness";
import AdminEnquiries from "@/pages/AdminEnquiries";
import AdminTeam from "@/pages/AdminTeam";
import AdminAudit from "@/pages/AdminAudit";
import AdminAccounting from "@/pages/AdminAccounting";
import AdminOrders from "@/pages/AdminOrders";
import AdminOrderDetail from "@/pages/AdminOrderDetail";
import AdminReturns from "@/pages/AdminReturns";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminImport from "@/pages/AdminImport";
import AdminBulkPhotos from "@/pages/AdminBulkPhotos";
import AdminNiftyImport from "@/pages/AdminNiftyImport";
import AdminSourcingROI from "@/pages/AdminSourcingROI";
import AdminSourcingTrips from "@/pages/AdminSourcingTrips";
import AdminSourcingTripDetail from "@/pages/AdminSourcingTripDetail";
import AdminCrossChannelPNL from "@/pages/AdminCrossChannelPNL";
import AdminPurchasingIntelligence from "@/pages/AdminPurchasingIntelligence";
import AdminMessages from "@/pages/AdminMessages";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

// Hoist static config outside render so consumers don't re-render on every parent tick.
const TOAST_OPTIONS = {
  style: {
    background: "#F6F2EB",
    border: "1px solid #B8A89A",
    borderRadius: 0,
    color: "#2E2C2A",
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.85rem",
    letterSpacing: "0.05em",
  },
};

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <SplashScreen />
          <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
          <Routes>
            <Route path="/" element={<Storefront />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/checkout/:itemId" element={<CheckoutPage />} />
            <Route path="/order/confirmation/:orderId" element={<OrderConfirmation />} />
            <Route path="/checkout/success/:sessionId" element={<CheckoutSuccess />} />
            <Route path="/privacy" element={<Privacy />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password/:token" element={<ResetPassword />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/inventory" element={<ProtectedRoute><AdminInventory /></ProtectedRoute>} />
            <Route path="/admin/inventory/bulk-photos" element={<ProtectedRoute><AdminBulkPhotos /></ProtectedRoute>} />
            <Route path="/admin/inventory/:id" element={<ProtectedRoute><AdminInventoryEdit /></ProtectedRoute>} />
            <Route path="/admin/import" element={<ProtectedRoute><AdminImport /></ProtectedRoute>} />
            <Route path="/admin/nifty-import" element={<ProtectedRoute><AdminNiftyImport /></ProtectedRoute>} />
            <Route path="/admin/analytics/sourcing" element={<ProtectedRoute><AdminSourcingROI /></ProtectedRoute>} />
            <Route path="/admin/sourcing-trips" element={<ProtectedRoute><AdminSourcingTrips /></ProtectedRoute>} />
            <Route path="/admin/sourcing-trips/:id" element={<ProtectedRoute><AdminSourcingTripDetail /></ProtectedRoute>} />
            <Route path="/admin/analytics/pnl" element={<ProtectedRoute><AdminCrossChannelPNL /></ProtectedRoute>} />
            <Route path="/admin/analytics/intelligence" element={<ProtectedRoute><AdminPurchasingIntelligence /></ProtectedRoute>} />
            <Route path="/admin/pipeline" element={<ProtectedRoute><AdminPipeline /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/orders/:id" element={<ProtectedRoute><AdminOrderDetail /></ProtectedRoute>} />
            <Route path="/admin/returns" element={<ProtectedRoute><AdminReturns /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/admin/mobile/capture" element={<ProtectedRoute><MobileCapture /></ProtectedRoute>} />
            <Route path="/admin/enquiries" element={<ProtectedRoute><AdminEnquiries /></ProtectedRoute>} />
            <Route path="/admin/accounting" element={<ProtectedRoute><AdminAccounting /></ProtectedRoute>} />
            <Route path="/admin/audit" element={<ProtectedRoute><AdminAudit /></ProtectedRoute>} />
            <Route path="/admin/messages" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
            <Route path="/admin/business" element={<ProtectedRoute><AdminBusiness /></ProtectedRoute>} />
            <Route path="/admin/team" element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />

            {/* Friendly shortcut so /mobile/capture (easy to type on a phone) reaches
                the rapid capture page. ProtectedRoute on the admin route handles the
                login round-trip via ?next= if the visitor isn't already signed in. */}
            <Route path="/mobile/capture" element={<Navigate to="/admin/mobile/capture" replace />} />
            <Route path="/mobile" element={<Navigate to="/admin/mobile/capture" replace />} />

            {/* Catch-all — any unknown URL sends the visitor back to the storefront
                instead of a blank ivory screen (React Router renders nothing when
                no route matches, which looks like an app-crash on mobile). */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <HelpDrawer />
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;
